import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Typography from "@material-ui/core/Typography/Typography";
import * as BasketAction from "../../stores/basket/BasketAction";
import { useAuthContext } from "../../auth/AuthProvider";
import { useHistory, useParams } from "react-router-dom";
import { styles } from "./BasketView.styles";
import { Backdrop, Dialog, DialogActions, DialogTitle, Step, StepLabel, Stepper, Tooltip, WithStyles, withStyles } from "@material-ui/core";
import { BasketViewOverview } from "./BasketViewOverview";
import { BasketViewAddress } from "./BasketViewAddress";
import { BasketViewOrder } from "./BasketViewOrder";
import { useTranslation } from "react-i18next";
import { isLoggedIn } from "../../utilities/userUtils";
import { SignInOrRegisterView } from "./SignInOrRegisterView";
import { BlueButton } from "../components/Buttons/BlueButton";
import * as UserDetailsAction from "../../stores/user/UserDetailsAction";
import { deliveryMethod } from "../../constants/ORDER_DETAILS";
import { CenteredContainer } from "../components/CenteredContainer/CenteredContainer";
import PermissionGuard from "../components/PermissionGuard/PermissionGuard";
import { PageBanner } from "../components/PageBanner/PageBanner";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../constants/ToastStatusEnum";
import CircularProgress from "@material-ui/core/CircularProgress/CircularProgress";
import { isAnyProductProhibitedForUser, isCCPaymentProductDisabled } from "./productOverview/ProductHelper";

/*
    This is the main view of the checkout, containing the stepper and the links to the following steps.
*/

export interface IBasketViewProps extends WithStyles<typeof styles> {
}

/*
log in should support the redirection to the basket and move on to the second step automatically
*/

const BasketViewPure: React.FunctionComponent<IBasketViewProps> = ({classes}) => {

    let {stepId} = useParams();
    stepId = Number(stepId);
    const changeUrlStep = step => {
        let path = "/basket/" + step;
        history.push(path);
    };

    const [openBackdrop, setOpenBackdrop] = React.useState(false);
    const [cardError, setCardError] = React.useState(null);
    useEffect(() => {
        if(cardError) {
            setOpenBackdrop(false);
        };
    }, [cardError]);

    const {t} = useTranslation();
    const {enqueueSnackbar} = useSnackbar();

    const history = useHistory();
    const authContext = useAuthContext();
    const dispatch = useDispatch();
    const basket = useSelector((state) => state.basket);
    const basketState = useSelector((state) => state.basket?.state);

    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const loggedInUser = isLoggedIn(authContext);

    const userId = authContext?.user?.id;

    const steps = [
        {main: t("general.basket"), sub: t("basket.review")},
        {main: t("general.address"), sub: t("basket.enter")},
        {main: t("basket.order"), sub: t("basket.confirm")}
    ];

    const [agrees, setAgrees] = React.useState({
        agreeTermsCondition: false,
        agreeClientLegal: false
    });

    const handleAgree = (event) => {
        setAgrees({
            ...agrees,
            [event.target.name]: !agrees[event.target.name]
        });
    };

    const [commitedToPay, setCommitedToPay] = React.useState(false);

    //get the basket content, including orderId
    useEffect(() => {
        dispatch(BasketAction.requestBasket(userId));
        if (loggedInUser) {
            dispatch(UserDetailsAction.requestCurrentUser(userId));
        }
    }, [dispatch, userId, loggedInUser]);

    const userDetails = useSelector(state => state.userDetails?.currentlyLoggedUser);
    const userQuota = useSelector(state => state.quotas?.userQuota);
    const productsInfo = useSelector((state) => state.products);

    const isCCPaymentDisabled = isCCPaymentProductDisabled(basket, productsInfo);
    
    //now we get the information about the order
    const [orderDetails, setOrderDetails] = React.useState({
        orderId: basket.orderId,
        userId: userId,
        orderState: basket.state,
        type: basket.type,
        adminFee: basket.adminFee || 0.00,
        discount: basket.discount || 0.00,
        vat: basket.vat,
        remark: basket.remark || "",
        delivery: deliveryMethod,
        paymentMethod: "",
        totalPrice: basket.totalPrice
    });

    const handleOrderChange = (event) => {
        setOrderDetails({
            ...orderDetails,
            [event.target.name]: event.target.value
        });
        if (event.target.name === "paymentMethod") {
            setCommitedToPay(true);
        }
    };

    const [checkoutEnabled, setCheckoutEnabled] = React.useState(false);
    const [deliveryAddressFilled, setDeliveryAddressFilled] = React.useState(false);
    const [vatIdFilled, setVatIdFilled] = React.useState(false);

    function getStepContent(step) {
        if (loggedInUser) {
            switch (step) {
                case 1:
                    return <BasketViewOverview basket={basket} buttons={true} userId={userId} orderDetails={orderDetails}
                                               handleOrderChange={handleOrderChange} comment={true} />;
                case 2:
                    return <BasketViewAddress userDetails={userDetails} buttons={true}
                                              setDeliveryAddressFilled={setDeliveryAddressFilled}
                                              setVatIdFilled={setVatIdFilled} />;
                case 3:
                    return <BasketViewOrder basket={basket} userId={userId} agrees={agrees} handleAgree={handleAgree}
                                            orderDetails={orderDetails} handleOrderChange={handleOrderChange}
                                            userDetails={userDetails} checkoutEnabled={checkoutEnabled} showBackendResponseError={showBackendResponseError} setCardError={setCardError} cardError={cardError} checkCardValidity={checkCardValidity} setCheckCardValidity={setCheckCardValidity} setCreditCardValidId={setCreditCardValidId} creditCardValidId={creditCardValidId} isCCPaymentProductDisabled={isCCPaymentDisabled}/>;
                default:
                    return "Unknown step";
            }
        } else {
            switch (step) {
                case 1:
                    return <BasketViewOverview basket={basket} buttons={true} userId={userId} orderDetails={orderDetails}
                                               handleOrderChange={handleOrderChange} comment={true} />;
                case 2:
                    return <SignInOrRegisterView />;
                default:
                    return "Unknown step";
            }
        }
    }

    const handleGoHome = () => {
        history.push("/");
    };

    const handleConfirm = () => {
        dispatch(BasketAction.requestBasket(userId));
        if (basketState === "Locked") {
            enqueueSnackbar("Can not empty the basket during the payment process!", {variant: ToastStatusEnum.Error});
        } else {
            dispatch(BasketAction.deleteAll(userId)).then((res) => {
                dispatch(BasketAction.requestBasket(userId));
            });
        };
    };

    const enableCardPaymentFromBackend = useSelector(state => state.basket?.backendAcceptedCardPayment);
    const [showBackendResponseError, setShowBackendResponseError] = useState(false);

    useEffect(() => {
        if (enableCardPaymentFromBackend !== undefined && enableCardPaymentFromBackend !== null) {
            setOpenBackdrop(false);
            setShowBackendResponseError(!enableCardPaymentFromBackend)
        };
    }, [enableCardPaymentFromBackend]);
    
    const [backendAcceptsPayment, setBackendAcceptsPayment] = useState(false);

    const [creditCardValidId, setCreditCardValidId] = useState(null);
    const [checkCardValidity, setCheckCardValidity] = useState(false);

    const handleCheckout = () => {
        setCardError(null);
        if (basketState === "Locked") {
            enqueueSnackbar("Your basket is locked due to the ongoing payment process. This might take up to 15 minutes.", {variant: ToastStatusEnum.Error});
        } else {
            //check if the credit card data is valid and save the cc id for unzer payment
            if(orderDetails.paymentMethod === "Card" && creditCardValidId === null) {
                setOpenBackdrop(true);
                setCheckCardValidity(true);                
            } else if (orderDetails.paymentMethod === "Quota" || orderDetails.paymentMethod === "BankTransfer") {
                let additionalInfo = {
                    paymentMethod: orderDetails.paymentMethod,
                    remark: orderDetails.remark || "",
                    ...(orderDetails.paymentMethod === "Quota" && {quotaId: userQuota?.id})
                };
                //make sure we save the updated version of the basket is in the local storage
                const checkoutBasket = {
                    ...basket,
                    paymentMethod: orderDetails.paymentMethod,
                    remark: orderDetails.remark || ""
                };
                localStorage.setItem("basket-checkout", JSON.stringify(checkoutBasket));
    
                dispatch(BasketAction.addAdditionalInfo(userId, additionalInfo))
                    .then(function () {
                        setBackendAcceptsPayment(true);
                    });
                    
                setAdditionalInfoSubmitted(true);
            };
        };
    };

    const [additionalInfoSubmitted, setAdditionalInfoSubmitted] = useState(false);

    useEffect(() => {
        if (creditCardValidId && !additionalInfoSubmitted && !backendAcceptsPayment) {
            setCheckCardValidity(false);
            let additionalInfo = {
                paymentMethod: orderDetails.paymentMethod,
                remark: orderDetails.remark || ""
            };
            //make sure we save the updated version of the basket is in the local storage
            const checkoutBasket = {
                ...basket,
                paymentMethod: orderDetails.paymentMethod,
                remark: orderDetails.remark || ""
            };
            localStorage.setItem("basket-checkout", JSON.stringify(checkoutBasket));

            dispatch(BasketAction.addAdditionalInfo(userId, additionalInfo))
                .then(function () {
                    setBackendAcceptsPayment(true);
                });
                
            setAdditionalInfoSubmitted(true);
        };
    }, [additionalInfoSubmitted, backendAcceptsPayment, basket, creditCardValidId, dispatch, orderDetails.paymentMethod, orderDetails.remark, userId, userQuota?.id]);

    useEffect(() => {
        if (enableCardPaymentFromBackend && backendAcceptsPayment) {
            dispatch(BasketAction.deleteBackendAcceptancePayment());
            if (orderDetails.paymentMethod === "Card") {
                dispatch(BasketAction.checkOut(userId))
                    .then(
                        (res) => {
                            setCardError(null);
                            setCheckoutEnabled(true);
                        }
                    )
                    .catch(
                        e => {
                            setCardError(e.message);
                        }
                    )
            } else if (orderDetails.paymentMethod === "BankTransfer" || orderDetails.paymentMethod === "Quota") {
                //enable the checkout without card payment
                history.push("/basket-status/" + String(basket.orderId));
            }
        };
    }, [enableCardPaymentFromBackend, backendAcceptsPayment, dispatch, orderDetails.paymentMethod, history, basket.orderId, enqueueSnackbar, userId]);

    const handleCreateEmptyOrder = () => {
        history.push("/admin/manage-orders/create");
    };

    const handleCreateOrderOnBehalf = () => {
        history.push("/admin/order-on-behalf");
    };

    if (!basket || !basket.items || basket.items?.length === 0) {
        return (
            <CenteredContainer>
                <Typography variant="h5">{t("basket.emptyBasket")}</Typography>
                <PermissionGuard scope="orders.write">
                    <BlueButton onClick={handleCreateEmptyOrder}>{t("admin.manageOrdersView.createEmptyOrder")}</BlueButton>
                </PermissionGuard>
            </CenteredContainer>
        );
    }

    const handleNext = () => {
        changeUrlStep(stepId + 1);
    };

    const handleBack = () => {
        changeUrlStep(stepId - 1);
    };

    const handleReset = () => {
        changeUrlStep(1);
    };

    const handleSetActiveStep = (index) => {
        if (loggedInUser === true) {
            changeUrlStep(index);
        }
    };

    function CustomIcons(props) {
        let classname = "";
        if (props.active) classname = classes.active;
        if (props.completed) classname = classes.completed;

        return (
            <svg
                className={`${classes.rootStep} ${classname}`}
                focusable="false"
                viewBox="0 0 24 24"
                aria-hidden="true"
                role="presentation"
                width="24px"
            >
                <circle cx="12" cy="12" r="12" />
                <text className={classes.text} x="12" y="18" textAnchor="middle">
                    {props.icon}
                </text>
            </svg>
        );
    };

    const isAnyProductProhibited = isAnyProductProhibitedForUser(basket, productsInfo, userDetails);

    return <>
        <PageBanner title={t("general.basket")} isPublic />
        <CenteredContainer>
            <Stepper className={classes.stepper} alternativeLabel activeStep={stepId - 1}>
                {steps.map((label, index) => (
                    <Step key={label.main} onClick={() => handleSetActiveStep(index)}>
                        <StepLabel StepIconComponent={CustomIcons}>
                            <Typography variant="h3">{label.main}</Typography>
                            <Typography variant="body2">{label.sub}</Typography>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            <div className={classes.stepContainer}>
                {stepId - 1 === steps.length
                    ? <div>
                        <Typography variant="body1" className={classes.instruction}>{t("basket.allStepsComplete")}</Typography>
                        <BlueButton
                            onClick={handleReset}
                        >
                            {t("basket.reset")}
                        </BlueButton>
                    </div>
                    : <div className={classes.stepContent}>
                        {getStepContent(stepId)}
                        <div className={classes.stepsButtons}>
                            <PermissionGuard scope="orders.write">
                                <BlueButton onClick={handleCreateOrderOnBehalf}>
                                    Create order from cart
                                </BlueButton>
                            </PermissionGuard>
                            <BlueButton
                                onClick={handleGoHome}
                            >
                                {t("basket.continueShopping")}
                            </BlueButton>
                            <BlueButton
                                onClick={() => setConfirmationDialogOpen(true)}
                            >
                                {t("basket.deleteCart")}
                            </BlueButton>
                            <BlueButton
                                disabled={stepId === 1}
                                onClick={handleBack}
                                style={{marginLeft: "30px"}}
                            >
                                {t("basket.back")}
                            </BlueButton>

                            <Tooltip
                                title={stepId - 1 === steps.length - 1 ? t("basket.validateCheckout") : ((stepId - 1 === steps.length - 2) && (!deliveryAddressFilled)) ? "Please fill in the delivery address first" : t("basket.goNext")}>
                                <span>
                                    <BlueButton
                                        disabled={
                                            ((stepId - 1 === steps.length - 1) && ((agrees.agreeTermsCondition && agrees.agreeClientLegal && commitedToPay) === false))
                                            || ((stepId - 1 === steps.length - 2) && (loggedInUser === false))
                                            || ((stepId - 1 === steps.length - 2) && (!deliveryAddressFilled))
                                            || ((stepId - 1 === steps.length - 2) && (!vatIdFilled))
                                            || isAnyProductProhibited
                                        }
                                        onClick={stepId - 1 === steps.length - 1 ? handleCheckout : handleNext}
                                    >
                                        {stepId - 1 === steps.length - 1 ? t("basket.checkout") : t("basket.next")}
                                    </BlueButton>
                                </span>
                            </Tooltip>
                        </div>

                        <Dialog open={confirmationDialogOpen}>
                            <DialogTitle id="confirm-dialog-title">{t("basket.areYouSure")}</DialogTitle>
                            <DialogActions>
                                <BlueButton
                                    onClick={handleConfirm}
                                >
                                    {t("general.yes")}
                                </BlueButton>
                                <BlueButton
                                    onClick={() => setConfirmationDialogOpen(false)}
                                >
                                    {t("general.no")}
                                </BlueButton>
                            </DialogActions>
                        </Dialog>
                    </div>
                }
            </div>

            <Backdrop
                style={{ color: '#fff', zIndex: 1000 }}
                open={openBackdrop}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

        </CenteredContainer>
    </>;
};

export const BasketView = withStyles(styles)(BasketViewPure);
